import memoize from "memoizee";
import { createSlice } from "@reduxjs/toolkit";
import { curatorShortcutsMap } from "../../../components/admin/curator/CuratorShortcuts/curatorShortcuts.config";
import { SHORTCUT_TYPE } from "../../../config/constant/unityConstants";
import { dispatcher } from "../../../helpers/projectHelper";
import { toast } from "react-toastify";
import { LogCurator } from "../../../helpers/logEvents";

const initialState = {
  popupOpen: false,
  materialCopied: false,
  activeTab: SHORTCUT_TYPE.BASIC,

  highlightSelected: true,
  showDimensions: false,
  lightsVisibility: true,

  replacePropActive: false,
  replaceGroupPropActive: false,
  replacePropWith: null,

  userCustomConfig: null,
  editingShortcutName: null,
  editShortcutKeys: null,
};

const shortcutsModule = createSlice({
  name: "shortcutsModule",
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setMaterialCopied: (state, { payload }) => {
      state.materialCopied = payload;
    },
    setActiveTab: (state, { payload }) => {
      state.activeTab = payload;
    },
    setPopupOpen: (state, { payload }) => {
      state.popupOpen = payload;
    },
    setHighlightSelected: (state, { payload }) => {
      state.highlightSelected = payload;
    },
    setShowDimensions: (state, { payload }) => {
      state.showDimensions = payload;
    },
    setLightsVisibility: (state, { payload }) => {
      state.lightsVisibility = payload;
    },
    setReplacePropActive: (state, { payload }) => {
      state.replacePropActive = payload;
    },
    setReplaceGroupPropActive: (state, { payload }) => {
      state.replaceGroupPropActive = payload;
    },
    setReplacePropWith: (state, { payload }) => {
      state.replacePropWith = payload;
    },
    setUserCustomConfig: (state, { payload }) => {
      state.userCustomConfig = payload;
    },
    setEditingShortcutName: (state, { payload }) => {
      state.editingShortcutName = payload;
    },
    setEditShortcutKeys: (state, { payload }) => {
      state.editShortcutKeys = payload;
    },
  },
});

export const {
  setMaterialCopied,
  setActiveTab,
  setPopupOpen,
  setHighlightSelected,
  setShowDimensions,
  setLightsVisibility,
  setReplacePropActive,
  setReplaceGroupPropActive,
  setReplacePropWith,
  setUserCustomConfig,
  setEditingShortcutName,
  setEditShortcutKeys,
} = shortcutsModule.actions;
export const shortcutsModuleActions = shortcutsModule.actions;
export const shortcutsModuleSelector = (state) => state.shortcutsModule;
export const shortcutsModuleReducer = shortcutsModule.reducer;
export default shortcutsModuleReducer;

export const selectIsMaterialCopied = (state) =>
  state.shortcutsModule.materialCopied;
export const selectActiveTab = (state) => state.shortcutsModule.activeTab;
export const selectPopupOpen = (state) => state.shortcutsModule.popupOpen;
export const selectHighlightSelected = (state) =>
  state.shortcutsModule.highlightSelected;
export const selectShowDimensions = (state) =>
  state.shortcutsModule.showDimensions;
export const selectLightsVisibility = (state) =>
  state.shortcutsModule.lightsVisibility;
export const selectReplacePropActive = (state) =>
  state.shortcutsModule.replacePropActive;
export const selectReplaceGroupPropActive = (state) =>
  state.shortcutsModule.replaceGroupPropActive;
export const selectReplacePropWith = (state) =>
  state.shortcutsModule.replacePropWith;
export const selectUserCustomConfig = (state) =>
  state.shortcutsModule.userCustomConfig;
export const selectEditingShortcutName = (state) =>
  state.shortcutsModule.editingShortcutName;
export const selectWaitingForNewCombination = (state) =>
  Boolean(state.shortcutsModule.editingShortcutName);
  export const selectEditShortcutKeysByName = (state, name) => {
    if (state.shortcutsModule.editingShortcutName !== name) return null;
    return state.shortcutsModule.editShortcutKeys;
  };
export const selectHasCustomConfig = (state) =>
  Boolean(
    state.shortcutsModule.userCustomConfig &&
      Object.keys(state.shortcutsModule.userCustomConfig).length
  );

export const selectShortcutEditing = (state, name) => {
  return state.shortcutsModule.editingShortcutName === name;
};

export const selectEditShortcutKeys = (state) => {
  return state.shortcutsModule.editShortcutKeys;
};

export const selectCombinedShortcutsConfig = (state) => {
  const userCustomConfig = state.shortcutsModule.userCustomConfig;
  return calculateConfig(userCustomConfig);
};

const calculateConfig = memoize(
  (userCustomConfig) => {
    if (!userCustomConfig) {
      return curatorShortcutsMap;
    }

    const configCopy = { ...curatorShortcutsMap };
    Object.keys(userCustomConfig).forEach((key) => {
      if (configCopy[key]) {
        configCopy[key] = {
          ...configCopy[key],
          keys: userCustomConfig[key]?.keys || configCopy[key].keys,
        };
      }
    });

    // after getting updated configCopy, checking for existing key & applying add new key
    Object.keys(userCustomConfig).forEach((key) => {
      let isExists = false;
      let isDefaultKey;
      Object.keys(configCopy).forEach((a) => {
        const isDifferentShortcut = key !== a;
        const isKeysSame =
          JSON.stringify(configCopy[a].keys) ===
          JSON.stringify(userCustomConfig[key].keys);
        if (isDifferentShortcut && isKeysSame) {
          isDefaultKey = a;
          isExists = true;
        }
      });

      if (isDefaultKey) {
        configCopy[isDefaultKey] = {
          ...configCopy[isDefaultKey],
          keys: [],
          addNewKey: true,
        };
      }
    });
    LogCurator("shortcuts configCopy", configCopy);

    return configCopy;
  },
  { length: 1 }
);

export const cancelEditing = () => {
  dispatcher(setEditingShortcutName(null));
  dispatcher(setEditShortcutKeys(null));
  toast.dismiss("TOAST_PLEASE_PRESS_SHORTKEY");
};
